import { ReactElement, useMemo, useState } from 'react'
import { difference, partition } from 'lodash-es'
import { SelectAll } from '@mui/icons-material'
import { Box, Button, Stack } from '@mui/material'
import { RichTreeViewPro } from '@mui/x-tree-view-pro'
import { LoadingIndicator } from '@ui/feedback'
import { Tabs } from '@ui/navigation'
import { getTemplateGuids, toTemplateTreeItems } from '../../utils'
import CustomTreeItem from '../CustomTreeItem'

interface Props {
  availableTemplates: Record<string, TemplatesResult> | undefined
  isLoading: boolean
  alreadyAddedTemplates: string[]
  selectedTemplates: string[]
  onChangeSelectedTemplates: (selected: string[]) => void
}

/** Left pane of the add-template dialog: the template tree per category */
const TemplateSelection = ({
  availableTemplates,
  isLoading,
  alreadyAddedTemplates,
  selectedTemplates,
  onChangeSelectedTemplates,
}: Props): ReactElement => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  const categoryNames = useMemo(() => Object.keys(availableTemplates ?? {}), [availableTemplates])
  const currentCategory =
    selectedCategory && categoryNames.includes(selectedCategory)
      ? selectedCategory
      : categoryNames[0]

  const currentTemplates = useMemo(
    () => availableTemplates?.[currentCategory]?.templates ?? [],
    [availableTemplates, currentCategory],
  )

  const items = useMemo(
    () => toTemplateTreeItems(currentTemplates, alreadyAddedTemplates),
    [currentTemplates, alreadyAddedTemplates],
  )

  const currentGuids = useMemo(() => getTemplateGuids(currentTemplates), [currentTemplates])

  // The selection spans all categories, the tree only edits the current one
  const [selectedInCurrentCategory, selectedInOtherCategories] = useMemo(() => {
    const current = new Set(currentGuids)
    return partition(selectedTemplates, guid => current.has(guid))
  }, [selectedTemplates, currentGuids])

  const selectInCurrentCategory = (guids: string[]) =>
    onChangeSelectedTemplates([...selectedInOtherCategories, ...guids])

  return (
    <Stack spacing={1} sx={{ height: '100%', minHeight: 0 }}>
      {categoryNames.length > 1 && (
        <Tabs
          items={categoryNames}
          value={currentCategory}
          onChange={setSelectedCategory}
          size="md"
          kind="folder"
        />
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <RichTreeViewPro
            data-cy="template-selection-tree"
            items={items}
            multiSelect
            checkboxSelection
            // The label toggles the selection, so only the arrow expands
            expansionTrigger="iconContainer"
            selectionPropagation={{ descendants: true }}
            slots={{ item: CustomTreeItem }}
            selectedItems={selectedInCurrentCategory}
            onSelectedItemsChange={(_, ids) => selectInCurrentCategory(ids)}
          />
        )}
      </Box>

      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => selectInCurrentCategory(difference(currentGuids, alreadyAddedTemplates))}
          endIcon={<SelectAll />}
        >
          Nicht hinzugefügte
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => selectInCurrentCategory(currentGuids)}
          endIcon={<SelectAll />}
        >
          Alle
        </Button>
        <Button size="small" onClick={() => selectInCurrentCategory([])}>
          Auswahl aufheben
        </Button>
      </Stack>
    </Stack>
  )
}

export default TemplateSelection
